import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import {
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronUp,
  Plus,
  Check,
  Loader2,
  Home,
  HardDrive
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FolderBrowser = ({ open, onClose, onSelect, initialPath = '/app/uploads' }) => {
  const { token } = useAuth();
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [parentPath, setParentPath] = useState(null);
  const [folders, setFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const fetchFolders = useCallback(async (path) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/folders`, {
        params: { path },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentPath(response.data.current_path);
      setParentPath(response.data.parent_path);
      setFolders(response.data.folders);
      setSelectedFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load folders');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchFolders(initialPath);
    }
  }, [open, initialPath, fetchFolders]);

  const handleNavigate = (path) => {
    fetchFolders(path);
  };

  const handleGoUp = () => {
    if (parentPath) {
      fetchFolders(parentPath);
    }
  };

  const handleSelect = (folder) => {
    setSelectedFolder(folder);
  };

  const handleDoubleClick = (folder) => {
    fetchFolders(folder.path);
  };

  const handleConfirm = () => {
    const pathToSelect = selectedFolder ? selectedFolder.path : currentPath;
    onSelect(pathToSelect);
    onClose();
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setIsCreating(true);
    try {
      const response = await axios.post(`${API}/admin/folders/create`, null, {
        params: { path: currentPath, name: newFolderName },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Folder created');
      setNewFolderName('');
      setShowNewFolder(false);
      fetchFolders(currentPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    } finally {
      setIsCreating(false);
    }
  };

  const quickPaths = [
    { name: 'Uploads', path: '/app/uploads', icon: Home },
    { name: 'App', path: '/app', icon: HardDrive },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg" data-testid="folder-browser-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">SELECT FOLDER</DialogTitle>
        </DialogHeader>

        {/* Quick Access */}
        <div className="flex gap-2 mb-4">
          {quickPaths.map(qp => (
            <Button
              key={qp.path}
              variant="outline"
              size="sm"
              onClick={() => handleNavigate(qp.path)}
              className={`border-zinc-700 text-xs ${currentPath === qp.path ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              <qp.icon className="w-3 h-3 mr-1" />
              {qp.name}
            </Button>
          ))}
        </div>

        {/* Current Path Display */}
        <div className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg mb-4">
          <div className="flex items-start gap-2">
            <Folder className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-500 uppercase mb-1">Current Path</p>
              <p 
                className="text-sm font-mono text-zinc-300 break-all leading-relaxed"
                title={currentPath}
              >
                {currentPath}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleGoUp}
            disabled={!parentPath || isLoading}
            className="border-zinc-700 text-zinc-400 hover:text-white"
            data-testid="folder-go-up"
          >
            <ChevronUp className="w-4 h-4 mr-1" />
            Up
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewFolder(true)}
            className="border-zinc-700 text-zinc-400 hover:text-white"
            data-testid="folder-new"
          >
            <Plus className="w-4 h-4 mr-1" />
            New Folder
          </Button>
        </div>

        {/* New Folder Input */}
        {showNewFolder && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-zinc-800/50 border border-zinc-700">
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="New folder name"
              className="flex-1 bg-zinc-900 border-zinc-700 h-9"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              data-testid="new-folder-input"
            />
            <Button
              size="sm"
              onClick={handleCreateFolder}
              disabled={isCreating || !newFolderName.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="create-folder-btn"
            >
              {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}
              className="text-zinc-400"
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Folder List */}
        <ScrollArea className="h-[300px] border border-zinc-800 bg-zinc-950/50">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 text-red-600 animate-spin" />
            </div>
          ) : folders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-zinc-500">
              <Folder className="w-12 h-12 mb-2 opacity-50" />
              <p>No subfolders</p>
              <p className="text-xs mt-1">Current folder will be selected</p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {folders.map(folder => (
                <button
                  key={folder.path}
                  onClick={() => handleSelect(folder)}
                  onDoubleClick={() => handleDoubleClick(folder)}
                  className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                    selectedFolder?.path === folder.path
                      ? 'bg-red-600/20 border border-red-600/50'
                      : 'hover:bg-zinc-800/50 border border-transparent'
                  }`}
                  data-testid={`folder-item-${folder.name}`}
                >
                  {selectedFolder?.path === folder.path ? (
                    <FolderOpen className="w-5 h-5 text-red-500 flex-shrink-0" />
                  ) : (
                    <Folder className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                  )}
                  <span className="flex-1 text-sm text-white truncate">{folder.name}</span>
                  {folder.has_children && (
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  )}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Selected Path Preview */}
        {selectedFolder && (
          <div className="mt-4 p-3 bg-red-600/10 border border-red-600/30 rounded-lg">
            <p className="text-xs text-zinc-400 uppercase mb-1">Selected Folder</p>
            <p className="text-sm font-mono text-white break-all leading-relaxed">{selectedFolder.path}</p>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            className="bg-red-600 hover:bg-red-700"
            data-testid="folder-select-confirm"
          >
            <Check className="w-4 h-4 mr-2" />
            {selectedFolder ? 'Select Folder' : 'Use Current Folder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FolderBrowser;
